import { Heading, ModalDialog, Page, PageContent } from '@zillow/constellation';
import { UserStatusType } from '../../graphql/queries';
import type { UserStatus } from '../../graphql/queries';
import { useGetReportsForApplication } from '../../hooks/useGetReports';
import { TallHeightLoadingMask } from '../../styles/styled-components';
import { BackgroundReport } from './BackgroundReport';
import {
  getBackgroundReportExpirationDate,
  getBackgroundReportRequestedDate,
  getCreditReportExpirationDate,
  getCreditReportRequestedDate,
} from '../../utils/user-statuses';
import { CreditReport } from './CreditReport';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { formatDate } from '../../utils/formatters';
import type { RetrieveReportsResponse } from '../../models';
import { transformReportData } from '../../utils/reports/report-utils';

interface ReportModalLoaderProps {
  reportType: string;
  userStatuses: Array<UserStatus>;
}

export function ReportModalLoader({ userStatuses, reportType }: ReportModalLoaderProps) {
  const router = useRouter();
  const { data } = useGetReportsForApplication();
  const reportData = transformReportData(data);

  function onClose() {
    const { pathname, query } = router;
    const { reportType: _, ...newQuery } = query;
    router.push({ pathname, query: newQuery });
  }

  return <ReportModal userStatuses={userStatuses} reportType={reportType} reportData={reportData} onClose={onClose} />;
}

const PrintSupportedModalDialog = styled(ModalDialog)`
  @media print {
    position: absolute;
    padding: 0;
    overflow-y: visible;
  }
`;

interface ReportModalProps {
  userStatuses: Array<UserStatus>;
  reportType: string;
  reportData?: RetrieveReportsResponse;
  onClose: () => void;
}

export function ReportModal({ reportType, userStatuses, reportData, onClose }: ReportModalProps) {
  const backgroundExpirationDate = getBackgroundReportExpirationDate(userStatuses);
  const backgroundRequestedDate = getBackgroundReportRequestedDate(userStatuses);
  const creditExpirationDate = getCreditReportExpirationDate(userStatuses);
  const creditRequestedDate = getCreditReportRequestedDate(userStatuses);

  return (
    <PrintSupportedModalDialog
      isOpen
      onClose={onClose}
      fullScreen
      shouldCloseOnOutsideClick={false}
      renderHeader={
        <Heading level={1} as="h1">
          {reportType === UserStatusType.backgroundReport ? 'Background check' : 'Credit report'}
        </Heading>
      }
      renderBody={
        <TallHeightLoadingMask loading={!reportData}>
          <Page backgroundColor="white">
            <PageContent className="fs-exclude">
              {reportData && (
                <>
                  {reportType === UserStatusType.backgroundReport && (
                    <BackgroundReport
                      backgroundReport={reportData.backgroundReport}
                      zipCode={reportData.zipCode}
                      requestedDateTime={formatDate(backgroundRequestedDate)}
                      expirationDateTime={formatDate(backgroundExpirationDate)}
                      requiresConditionalApproval={reportData.requiresConditionalApproval}
                    />
                  )}
                  {reportType === UserStatusType.creditReport && (
                    <CreditReport
                      creditReport={reportData.creditReport}
                      requestedDateTime={formatDate(creditRequestedDate)}
                      expirationDateTime={formatDate(creditExpirationDate)}
                    />
                  )}
                </>
              )}
            </PageContent>
          </Page>
        </TallHeightLoadingMask>
      }
    />
  );
}
